import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { X, Search, UserRound, User, UserPlus, Loader2, Banknote, CreditCard, SplitSquareHorizontal, FlaskConical } from "lucide-react";

const money = (n) => `£${Number(n || 0).toFixed(2)}`;

export default function EposCheckout({ open, onClose, basket, registerId, cfg, onComplete }) {
  const canDiscount = !!cfg?.permissions?.discount;
  const [custQuery, setCustQuery] = useState("");
  const [results, setResults] = useState([]);
  const [customer, setCustomer] = useState(null); // null => walk-in
  const [showNewCust, setShowNewCust] = useState(false);
  const [newCust, setNewCust] = useState({ name: "", email: "", phone: "" });
  const [savingCust, setSavingCust] = useState(false);
  const [discount, setDiscount] = useState({ type: "", value: "" });
  const [quote, setQuote] = useState(null);
  const [loadingQuote, setLoadingQuote] = useState(false);
  const [payMode, setPayMode] = useState("cash");
  const [cashTender, setCashTender] = useState("");
  const [splitCash, setSplitCash] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const lines = useMemo(() => basket.map((b) => ({
    title: b.name, unit_price: b.price || 0, qty: b.qty, vat_rate: 0.2,
    sku: b.sku, wc_id: b.wc_id, wc_variation_id: b.wc_variation_id,
    product_legacy_id: b.product_legacy_id || null, is_bundle: !!b.is_bundle,
  })), [basket]);

  const fetchQuote = useCallback(async () => {
    if (!open || lines.length === 0) return;
    setLoadingQuote(true);
    try {
      const body = { lines };
      if (canDiscount && discount.type && Number(discount.value) > 0)
        body.discount = { type: discount.type, value: Number(discount.value) };
      const r = await api.post("/epos/checkout/quote", body);
      setQuote(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not price basket"); }
    finally { setLoadingQuote(false); }
  }, [open, lines, discount, canDiscount]);

  useEffect(() => { fetchQuote(); }, [fetchQuote]);

  useEffect(() => {
    if (!custQuery || custQuery.length < 2) { setResults([]); return; }
    const t = setTimeout(async () => {
      try { const r = await api.get("/epos/customers", { params: { q: custQuery } }); setResults(r.data.items || []); }
      catch (e) { /* ignore */ }
    }, 250);
    return () => clearTimeout(t);
  }, [custQuery]);

  const total = quote?.total || 0;

  const createCustomer = async () => {
    const name = newCust.name.trim();
    if (!name) { toast.error("Customer name is required"); return; }
    setSavingCust(true);
    try {
      const r = await api.post("/epos/customers", { name, email: newCust.email.trim(), phone: newCust.phone.trim() });
      setCustomer(r.data);
      setShowNewCust(false);
      setNewCust({ name: "", email: "", phone: "" });
      toast.success("Customer added");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not add customer"); }
    finally { setSavingCust(false); }
  };

  const change = payMode === "cash" && cashTender ? Math.max(0, Number(cashTender) - total) : 0;
  const splitCard = payMode === "split" ? Math.max(0, total - Number(splitCash || 0)) : 0;

  const complete = async () => {
    if (!quote) return;
    let payments = [];
    if (payMode === "cash") {
      if (Number(cashTender) + 0.001 < total) { toast.error("Cash tendered is less than the total"); return; }
      payments = [{ method: "cash", amount: Number(cashTender) }];
    } else if (payMode === "card") {
      payments = [{ method: "card", amount: total }];
    } else {
      payments = [{ method: "cash", amount: Number(splitCash || 0) }, { method: "card", amount: Number(splitCard.toFixed(2)) }];
    }
    const body = {
      lines, payments,
      walk_in: !customer, customer_id: customer?.id || null,
      idempotency_key: (crypto?.randomUUID?.() || String(Date.now() + Math.random())),
    };
    if (canDiscount && discount.type && Number(discount.value) > 0)
      body.discount = { type: discount.type, value: Number(discount.value) };
    setSubmitting(true);
    try {
      const r = await api.post(`/epos/registers/${registerId}/checkout`, body);
      onComplete(r.data.transaction);
    } catch (e) { toast.error(e?.response?.data?.detail || "Checkout failed"); }
    finally { setSubmitting(false); }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose} data-testid="epos-checkout-modal">
      <div className="bg-white rounded-xl w-full max-w-3xl max-h-[92vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 h-16 border-b border-slate-100">
          <div className="flex items-center gap-2.5 text-xl font-bold text-slate-900">Checkout
            <span className="inline-flex items-center gap-1 text-xs font-bold px-2.5 py-1 rounded bg-amber-100 text-amber-700"><FlaskConical className="w-4 h-4" /> TEST MODE</span>
          </div>
          <button onClick={onClose}><X className="w-6 h-6 text-slate-400 hover:text-slate-700" /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Customer */}
          <div>
            <div className="text-xs font-bold uppercase text-slate-400 mb-2">Customer</div>
            {customer ? (
              <div className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2" data-testid="epos-selected-customer">
                <span className="flex items-center gap-2 text-sm"><UserRound className="w-4 h-4 text-indigo-500" /> {customer.name} <span className="text-slate-400">{customer.email || customer.phone || ""}</span></span>
                <button onClick={() => setCustomer(null)} className="text-xs text-slate-400 hover:text-rose-500">Use Walk-in</button>
              </div>
            ) : (
              <>
                <div className="flex items-center gap-2 text-sm text-slate-500 mb-2"><User className="w-4 h-4" /> Walk-in Customer (no details required)</div>
                <div className="relative">
                  <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                  <input value={custQuery} onChange={(e) => setCustQuery(e.target.value)} placeholder="Search customer by name, email or phone…"
                    data-testid="epos-customer-search" className="w-full pl-9 pr-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300" />
                </div>
                {results.length > 0 && (
                  <div className="mt-1 border border-slate-100 rounded-lg divide-y max-h-40 overflow-y-auto">
                    {results.map((c) => (
                      <button key={c.id} onClick={() => { setCustomer(c); setResults([]); setCustQuery(""); }}
                        data-testid={`epos-customer-${c.id}`} className="w-full text-left px-3 py-2 text-sm hover:bg-indigo-50">
                        {c.name} <span className="text-slate-400 text-xs">{c.email || c.phone || ""}</span>
                      </button>
                    ))}
                  </div>
                )}
                {!showNewCust ? (
                  <button onClick={() => { setShowNewCust(true); setNewCust((n) => ({ ...n, name: custQuery })); }}
                    data-testid="epos-new-customer-btn"
                    className="mt-2 inline-flex items-center gap-1.5 text-sm font-medium text-indigo-600 hover:text-indigo-700">
                    <UserPlus className="w-4 h-4" /> Add new customer
                  </button>
                ) : (
                  <div className="mt-2 rounded-lg border border-indigo-200 bg-indigo-50/40 p-3 space-y-2" data-testid="epos-new-customer-form">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-bold uppercase text-slate-500">New customer</span>
                      <button onClick={() => setShowNewCust(false)} className="text-xs text-slate-400 hover:text-rose-500">Cancel</button>
                    </div>
                    <input value={newCust.name} onChange={(e) => setNewCust((n) => ({ ...n, name: e.target.value }))}
                      placeholder="Full name *" data-testid="epos-new-customer-name"
                      className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm" />
                    <input value={newCust.email} onChange={(e) => setNewCust((n) => ({ ...n, email: e.target.value }))}
                      type="email" placeholder="Email" data-testid="epos-new-customer-email"
                      className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm" />
                    <input value={newCust.phone} onChange={(e) => setNewCust((n) => ({ ...n, phone: e.target.value }))}
                      placeholder="Telephone" data-testid="epos-new-customer-phone"
                      className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm" />
                    <button onClick={createCustomer} disabled={savingCust || !newCust.name.trim()}
                      data-testid="epos-new-customer-save"
                      className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50">
                      {savingCust ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving…</> : <><UserPlus className="w-4 h-4" /> Save & select</>}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Discount */}
          {canDiscount && (
            <div>
              <div className="text-xs font-bold uppercase text-slate-400 mb-2">Discount</div>
              <div className="flex gap-2">
                <select value={discount.type} onChange={(e) => setDiscount((d) => ({ ...d, type: e.target.value }))}
                  data-testid="epos-discount-type" className="px-3 py-2 rounded-lg border border-slate-200 text-sm">
                  <option value="">None</option>
                  <option value="percent">Percent %</option>
                  <option value="fixed">Fixed £</option>
                </select>
                <input value={discount.value} onChange={(e) => setDiscount((d) => ({ ...d, value: e.target.value }))}
                  type="number" step="0.01" placeholder="0" disabled={!discount.type}
                  data-testid="epos-discount-value" className="w-28 px-3 py-2 rounded-lg border border-slate-200 text-sm disabled:bg-slate-50" />
              </div>
            </div>
          )}

          {/* Totals */}
          <div className="rounded-lg bg-slate-50 border border-slate-100 p-5 text-base" data-testid="epos-checkout-totals">
            {loadingQuote ? <div className="flex items-center gap-2 text-slate-400"><Loader2 className="w-4 h-4 animate-spin" /> Pricing…</div> : quote ? (
              <div className="space-y-2">
                <div className="flex justify-between text-slate-500"><span>Subtotal (net)</span><span>{money(quote.subtotal_net)}</span></div>
                {quote.discount?.amount > 0 && <div className="flex justify-between text-rose-600"><span>Discount</span><span>-{money(quote.discount.amount)}</span></div>}
                <div className="flex justify-between text-slate-500"><span>VAT</span><span>{money(quote.vat_total)}</span></div>
                <div className="flex justify-between text-xl font-bold text-slate-900 pt-2 border-t border-slate-200"><span>Total</span><span data-testid="epos-checkout-total">{money(quote.total)}</span></div>
              </div>
            ) : null}
          </div>

          {/* Payment */}
          <div>
            <div className="text-xs font-bold uppercase text-slate-400 mb-2">Simulated payment</div>
            <div className="flex gap-2 mb-3">
              {[["cash", "Cash", Banknote], ["card", "Card", CreditCard], ["split", "Split", SplitSquareHorizontal]].map(([m, label, Icon]) => (
                <button key={m} onClick={() => setPayMode(m)} data-testid={`epos-pay-${m}`}
                  className={`flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-lg text-sm font-medium border ${payMode === m ? "bg-indigo-600 text-white border-indigo-600" : "bg-white text-slate-600 border-slate-200"}`}>
                  <Icon className="w-4 h-4" /> {label}
                </button>
              ))}
            </div>
            {payMode === "cash" && (
              <div className="flex items-center justify-between gap-3">
                <input value={cashTender} onChange={(e) => setCashTender(e.target.value)} type="number" step="0.01" placeholder="Cash tendered"
                  data-testid="epos-cash-tender" className="flex-1 px-3 py-2 rounded-lg border border-slate-200 text-sm" />
                <div className="text-sm text-slate-600">Change: <span className="font-bold text-emerald-600" data-testid="epos-cash-change">{money(change)}</span></div>
              </div>
            )}
            {payMode === "split" && (
              <div className="flex items-center gap-3">
                <label className="text-sm text-slate-500 flex items-center gap-2">Cash
                  <input value={splitCash} onChange={(e) => setSplitCash(e.target.value)} type="number" step="0.01"
                    data-testid="epos-split-cash" className="w-28 px-3 py-2 rounded-lg border border-slate-200 text-sm" /></label>
                <div className="text-sm text-slate-600">Card: <span className="font-bold" data-testid="epos-split-card">{money(splitCard)}</span></div>
              </div>
            )}
            <p className="text-[11px] text-amber-600 mt-2 flex items-center gap-1"><FlaskConical className="w-3 h-3" /> All payments are simulated. No real gateway or card terminal is used.</p>
          </div>
        </div>

        <div className="border-t border-slate-100 p-5">
          <button onClick={complete} disabled={submitting || !quote || loadingQuote} data-testid="epos-complete-btn"
            className="w-full inline-flex items-center justify-center gap-2 px-4 py-4 rounded-lg text-lg font-semibold text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50">
            {submitting ? <><Loader2 className="w-5 h-5 animate-spin" /> Completing…</> : <>Complete test transaction · {money(total)}</>}
          </button>
        </div>
      </div>
    </div>
  );
}
